import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { FlatService, TokenStorageService } from '../../core/services';
import { finalize } from 'rxjs/operators';
import { EntityListComponent, EntityListMenuAction, TableHeaderAndValueKeySets } from '../../core/ui/entity-list/entity-list.component';
import { SubscriptionService, SubscriptionUsageSummary } from '../../core/services/subscription.service';
import {
  appendUniqueById,
  createListPaginationState,
  hasMoreByTotal,
  resolveRequestPage,
  resetListPaginationState,
  updateAfterFetch
} from '../../core/utils/list-pagination.util';
import { NewEditFlatComponent } from './new-edit-flat/new-edit-flat.component';
import { ConfirmActionModalComponent } from '../../core/ui/confirm-action-modal/confirm-action-modal.component';
import { TranslatePipe } from '../../core/pipes/translate.pipe';

interface Flat {
  id: number;
  house?: { id?: number; houseName?: string; houseNo?: string } | null;
  flatName: string;
  floorName?: string;
  payDayTh?: number;
  rentAmount?: number;
  gasBillAmount?: number;
  electricBillAmount?: number;
  waterBillAmount?: number;
  serviceChargeAndOtherAmount?: number;
  negativeChargeAmount?: number;
  negativeChargeName?: string;
  isCurrentMonthBilling?: boolean;
  isGasBillRequired?: boolean;
  isElectricBillRequired?: boolean;
  isWaterBillRequired?: boolean;
  isServiceChargeAndOtherRequired?: boolean;
  isNegativeChargeRequired?: boolean;
  status: number;
  createdOn: string;
  updatedOn: string | null;
}

interface ApiResponse {
  code: string;
  message: string;
  refId: string | null;
  totalRows: number;
  data: Flat[];
  totals: any;
}

interface FlatListRow extends Flat {
  nameTopLine: string;
  nameBottomLine: string;
  rentAmountLabel: string;
  paymentStatusLabel: string;
}

@Component({
  selector: 'app-flat-list',
  standalone: true,
  imports: [CommonModule, EntityListComponent, NewEditFlatComponent, ConfirmActionModalComponent, TranslatePipe],
  templateUrl: './flat-list.component.html',
  styleUrls: ['./flat-list.component.css']
})
export class FlatListComponent implements OnInit, OnDestroy {
  isAuthenticated = false;
  isLoading = false;
  isLoadingMore = false;
  flatList: FlatListRow[] = [];
  subscriptionSummary: SubscriptionUsageSummary | null = null;
  error: string | null = null;
  status: string | null = null;
  filters: Record<string, any> = {};
  private readonly paginationState = createListPaginationState();
  private summaryRefreshSubscription?: Subscription;
  selectedFlat: Flat | null = null;
  pendingDeleteFlat: Flat | null = null;
  isDetailsOpen = false;
  isDeleting = false;
  isEditOpen = false;
  flatForEdit: Flat | null = null;
  tableHeaderAndValueKeySets: TableHeaderAndValueKeySets = {
    colorKey: 'status',
    'table.name': {
      topLineKey: 'nameTopLine',
      bottomLineKyes: ['nameBottomLine']
    },
    'table.totalIncome': 'rentAmountLabel',
    'table.paymentStatus': 'paymentStatusLabel'
  };
  readonly flatMenuActions: EntityListMenuAction[] = [
    { key: 'edit', label: 'action.edit' },
    { key: 'delete', label: 'action.delete', danger: true, requiredService: 'FLAT_DELETE' }
  ];

  get deletedMode(): boolean {
    return Number(this.filters?.['status']) === 0;
  }

  constructor(
    private readonly flats: FlatService,
    private readonly tokens: TokenStorageService,
    private readonly subscriptionService: SubscriptionService
  ) {}

  ngOnInit(): void {
    this.isAuthenticated = this.tokens.hasActiveSession;
    if (this.isAuthenticated) {
      this.status = 'Loading flat list...';
      this.loadSubscriptionSummary();
      this.summaryRefreshSubscription = this.subscriptionService.summaryRefresh$.subscribe(() => {
        this.loadSubscriptionSummary();
      });
      this.fetchFlats(true);
    } else {
      this.error = 'Please log in first.';
    }
  }

  ngOnDestroy(): void {
    this.summaryRefreshSubscription?.unsubscribe();
  }

  get hasMoreFlats(): boolean {
    return hasMoreByTotal(this.flatList, this.paginationState);
  }

  get flatCreateLimit(): number {
    return Number(this.subscriptionSummary?.currentPackage?.maxActiveFlat ?? 0);
  }

  get activeFlatCount(): number {
    return Number(this.subscriptionSummary?.usage?.activeFlatCount ?? 0);
  }

  get isCreateDisabled(): boolean {
    return this.flatCreateLimit > 0 && this.activeFlatCount >= this.flatCreateLimit;
  }

  showCreateLimitMessage(): string {
    return `Flat create limit is ${this.flatCreateLimit}.`;
  }

  fetchFlats(reset = true): void {
    if ((reset && this.isLoading) || (!reset && (this.isLoading || this.isLoadingMore || !this.hasMoreFlats))) {
      return;
    }

    if (reset) {
      this.isLoading = true;
      this.error = null;
      this.flatList = [];
      resetListPaginationState(this.paginationState);
    } else {
      this.isLoadingMore = true;
    }

    const filters = { ...this.filters };
    if (!Object.prototype.hasOwnProperty.call(filters, 'status')) {
      filters['status'] = 1;
    }
    const requestPage = resolveRequestPage(this.paginationState, reset);
    this.flats.list({
      pagination: this.paginationState.pagination,
      pageNo: requestPage,
      rowCount: this.paginationState.rowCount,
      filters
    })
      .pipe(finalize(() => {
        if (reset) {
          this.isLoading = false;
        } else {
          this.isLoadingMore = false;
        }
      }))
      .subscribe({
        next: (res: ApiResponse) => {
          if (res && res.code === '000') {
            const nextRows = (res.data || []).map((flat) => ({
              ...flat,
              nameTopLine: flat.flatName || 'N/A',
              nameBottomLine: this.buildNameBottomLine(flat),
              rentAmountLabel: this.formatAmount(flat.rentAmount),
              paymentStatusLabel: this.paymentStatus(flat)
            }));
            updateAfterFetch(this.paginationState, res?.totalRows, requestPage, nextRows.length);
            this.flatList = reset ? nextRows : appendUniqueById(this.flatList, nextRows);
            this.status = `Total flats: ${this.paginationState.totalRows || this.flatList.length}`;
          } else {
            this.error = res?.message || 'Unknown error occurred.';
          }
        },
        error: (err: any) => {
          this.error = err.message || 'Error fetching flats';
        },
      });
  }

  setFilters(filters: Record<string, any>): void {
    this.filters = { ...filters };
    this.fetchFlats(true);
  }

  onLoadMoreFromList(): void {
    this.fetchFlats(false);
  }

  onCardClickFromList(item: Record<string, any>): void {
    this.selectedFlat = item as Flat;
    this.isDetailsOpen = true;
  }

  onEditClickFromList(item: Record<string, any>): void {
    this.flatForEdit = item as Flat;
    this.isEditOpen = true;
  }

  onDeleteClickFromList(item: Record<string, any>): void {
    if (this.isDeleting) {
      return;
    }
    this.pendingDeleteFlat = item as Flat;
  }

  closeDeleteModal(): void {
    if (this.isDeleting) {
      return;
    }
    this.pendingDeleteFlat = null;
  }

  confirmDeleteFlat(): void {
    const flat = this.pendingDeleteFlat;
    if (!flat || this.isDeleting) {
      return;
    }

    this.isDeleting = true;
    this.error = null;
    this.flats.delete({ id: flat.id })
      .pipe(finalize(() => (this.isDeleting = false)))
      .subscribe({
        next: () => {
          this.pendingDeleteFlat = null;
          this.fetchFlats(true);
        },
        error: (err: any) => {
          this.pendingDeleteFlat = null;
          this.error = err?.message || 'Failed to delete flat.';
        }
      });
  }

  closeDetailsModal(): void {
    this.isDetailsOpen = false;
  }

  closeEditModal(): void {
    this.isEditOpen = false;
    this.flatForEdit = null;
  }

  onEditSave(): void {
    this.isEditOpen = false;
    this.flatForEdit = null;
    this.fetchFlats(true);
  }

  formatDateTime(value: string | null | undefined): string {
    if (!value) {
      return 'N/A';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return 'N/A';
    }

    const day = `${date.getDate()}`.padStart(2, '0');
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const year = date.getFullYear();
    const rawHours = date.getHours();
    const minutes = `${date.getMinutes()}`.padStart(2, '0');
    const period = rawHours >= 12 ? 'pm' : 'am';
    const hours12 = rawHours % 12 || 12;
    const hours = `${hours12}`.padStart(2, '0');

    return `${day}-${month}-${year} ${hours}:${minutes} ${period}`;
  }

  private formatAmount(value: unknown): string {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
      return 'N/A';
    }

    return `৳${Number(value).toLocaleString('en-US')}`;
  }

  private buildNameBottomLine(flat: Flat): string {
    const parts = [
      flat.floorName ? `Floor #${flat.floorName}` : '',
      flat.house?.houseNo ? `House #${flat.house.houseNo}` : '',
      flat.house?.houseName ? `${flat.house.houseName}` : ''
    ].filter((value) => value.length > 0);

    return parts.join(', ');
  }

  private paymentStatus(flat: Flat): string {
    if (flat.status !== 1) {
      return 'Inactive';
    }
    return flat.isCurrentMonthBilling ? 'Paid (Current Month)' : 'Remain (Current Month)';
  }

  private loadSubscriptionSummary(): void {
    this.subscriptionService.getSubscriptionSummary().subscribe({
      next: (summary) => {
        this.subscriptionSummary = summary;
      },
      error: () => {
        this.subscriptionSummary = null;
      }
    });
  }
}

